import { useEffect, useMemo, useState } from 'react'

type UseAvailableHeightArgs = {
  headerRef: React.RefObject<HTMLElement | null>
  toolbarRef?: React.RefObject<HTMLElement | null> // only if you can grab it; otherwise use a constant
  toolbarPinned?: boolean
  min?: number
  extraGutters?: number // padding/margins you want to subtract
}

export function useAvailableHeight({
  headerRef,
  toolbarRef,
  toolbarPinned = false,
  min = 240,
  extraGutters = 0,
}: UseAvailableHeightArgs) {
  const [wh, setWh] = useState<number>(() => window.innerHeight)
  const [headerH, setHeaderH] = useState(0)
  const [toolbarH, setToolbarH] = useState(0)

  // Observe element sizes (no layout thrash)
  useEffect(() => {
    const r1 = headerRef.current ? new ResizeObserver(([e]) => setHeaderH(Math.round(e.contentRect.height))) : null
    if (headerRef.current && r1) r1.observe(headerRef.current)

    const r2 =
      toolbarPinned && toolbarRef?.current
        ? new ResizeObserver(([e]) => setToolbarH(Math.round(e.contentRect.height)))
        : null
    if (toolbarPinned && toolbarRef?.current && r2) r2.observe(toolbarRef.current)

    const onResize = () => setWh(window.innerHeight)
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      r1?.disconnect()
      r2?.disconnect()
    }
  }, [headerRef, toolbarRef, toolbarPinned])

  const px = useMemo(() => {
    const usedToolbar = toolbarPinned ? toolbarH : 0
    return Math.max(min, wh - headerH - usedToolbar - extraGutters)
  }, [wh, headerH, toolbarH, toolbarPinned, min, extraGutters])

  // return both a number and a css string for convenience
  return { heightPx: px, heightCss: `${px}px` }
}
